import { isValidElement, useId } from 'react';

import { styled } from '@mui/material';

import { InfoButton } from '../InfoButton';
import { Radio } from '../Radio';
import { Status } from '../Status';
import { TooltipContainer } from '../TooltipContainer';
import { radioItemClasses } from './theme';

import type { ReactElement } from 'react';
import type { InfoButtonProps } from '../InfoButton';
import type { StatusProps } from '../Status';
import type { RadioItemOwnerState, RadioItemProps } from './types';

export function RadioItem(props: RadioItemProps) {
  const {
    checked,
    className,
    defaultChecked,
    disabled,
    disabledTooltip,
    error,
    id: idProp,
    info: infoProp,
    inputRef,
    name,
    onBlur,
    onChange,
    onClick,
    readOnly,
    ref,
    slotProps,
    status: statusProp,
    style,
    subText,
    subTextDirection = 'horizontal',
    text,
    value,
    ...others
  } = props;

  const { radio: radioSlotProps } = slotProps ?? {};

  const ownerState: RadioItemOwnerState = props;

  const fallbackId = useId();
  const id = idProp ?? fallbackId;

  let status = statusProp;
  if (isValidElement(statusProp) && statusProp.type === Status) {
    const element = statusProp as ReactElement<StatusProps>;
    status = (
      <RadioItemStatus
        {...element.props}
        size={element.props.size ?? 'sm'}
        ownerState={ownerState}
      />
    );
  }

  let info = infoProp;
  if (isValidElement(infoProp) && infoProp.type === InfoButton) {
    const element = infoProp as ReactElement<InfoButtonProps>;
    info = (
      <RadioItemInfoButton
        {...element.props}
        size={element.props.size ?? 'md'}
        color={element.props.color ?? 'default'}
        ownerState={ownerState}
        disabled={disabled}
      />
    );
  }

  return (
    <TooltipContainer message={disabled ? disabledTooltip : undefined}>
      <RadioItemRoot
        className={className}
        onClick={onClick}
        ref={ref}
        style={style}
        ownerState={ownerState}
        {...others}
      >
        <RadioItemRadio
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          error={error}
          id={id}
          inputRef={inputRef}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          readOnly={readOnly}
          value={value}
          ownerState={ownerState}
          {...radioSlotProps}
        />
        {(text || status || subText || info) && (
          <RadioItemTextGroup ownerState={ownerState}>
            <RadioItemValueRow ownerState={ownerState}>
              <RadioItemLabel htmlFor={id} ownerState={ownerState}>
                {text && <RadioItemText ownerState={ownerState}>{text}</RadioItemText>}
              </RadioItemLabel>
              {status && (
                <RadioItemStatusWrapper ownerState={ownerState}>{status}</RadioItemStatusWrapper>
              )}
              {info}
              {subText && subTextDirection === 'horizontal' && (
                <RadioItemSubText ownerState={ownerState}>{subText}</RadioItemSubText>
              )}
            </RadioItemValueRow>
            {subText && subTextDirection === 'vertical' && (
              <RadioItemSubText ownerState={ownerState}>{subText}</RadioItemSubText>
            )}
          </RadioItemTextGroup>
        )}
      </RadioItemRoot>
    </TooltipContainer>
  );
}

const RadioItemRoot = styled('div', {
  target: radioItemClasses.root,
})<{ ownerState: RadioItemOwnerState }>`
  display: inline-flex;
  align-items: ${({ ownerState }) =>
    ownerState.subText && ownerState.subTextDirection === 'vertical' ? 'flex-start' : 'center'};
  max-width: 100%;
`;

const RadioItemRadio = styled(Radio, {
  target: radioItemClasses.radio,
})<{ ownerState: RadioItemOwnerState }>`
  margin: 8px 8px 8px 0px;
`;

const RadioItemLabel = styled('label', {
  target: radioItemClasses.label,
})<{ ownerState: RadioItemOwnerState }>`
  min-height: 21px;
  cursor: ${({ ownerState }) => (ownerState.disabled ? 'initial' : 'pointer')};
`;

const RadioItemTextGroup = styled('div', {
  target: radioItemClasses.textGroup,
})<{ ownerState: RadioItemOwnerState }>`
  display: inline-flex;
  flex-direction: column;
  align-items: ${({ ownerState }) =>
    ownerState.subText && ownerState.subTextDirection === 'vertical' ? 'flex-start' : 'center'};
  max-width: 100%;
`;

const RadioItemValueRow = styled('span', {
  target: radioItemClasses.valueRow,
})<{ ownerState: RadioItemOwnerState }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: ${({ ownerState }) =>
    ownerState.subText && ownerState.subTextDirection === 'vertical' ? '32px' : '21px'};
`;

const RadioItemText = styled('span', {
  target: radioItemClasses.text,
})<{ ownerState: RadioItemOwnerState }>`
  ${({ theme }) => theme.typography['body/normal/md']};
  color: ${({ theme, ownerState }) =>
    ownerState.disabled ? theme.color['text/disabled'] : theme.color['text/default']};
`;

const RadioItemSubText = styled('span', {
  target: radioItemClasses.subText,
})<{ ownerState: RadioItemOwnerState }>`
  ${({ theme }) => theme.typography['body/normal/xs']};
  color: ${({ theme, ownerState }) =>
    ownerState.disabled ? theme.color['text/disabled'] : theme.color['text/weaker/default']};
  white-space: normal;
  word-break: break-word;
`;

const RadioItemStatusWrapper = styled('span', {
  target: radioItemClasses.statusWrapper,
})<{ ownerState: RadioItemOwnerState }>`
  opacity: ${({ ownerState }) => (ownerState.disabled ? 0.3 : 1)};
`;

const RadioItemStatus = styled(Status, {
  target: radioItemClasses.status,
})<{ ownerState: RadioItemOwnerState }>``;

const RadioItemInfoButton = styled(InfoButton, {
  target: radioItemClasses.infoButton,
})<{ ownerState: RadioItemOwnerState }>`
  & svg {
    color: ${({ theme, ownerState }) =>
      ownerState.disabled ? theme.color['icon/disabled'] : theme.color['icon/weaker/default']};
  }

  .${radioItemClasses.label} + & {
    margin-left: 4px;
  }
`;
